import { Howl, Howler } from 'howler'

type SoundName =
  | 'collect'
  | 'boost'
  | 'death'
  | 'kill'
  | 'portal_enter'
  | 'portal_spawn'
  | 'zone_warning'
  | 'zone_damage'
  | 'countdown'
  | 'respawn'
  | 'ui_click'
  | 'ui_hover'
  | 'ui_back'
  | 'ui_start'
  | 'ui_toggle'

type MusicName = 'menu' | 'gameplay' | 'intense' | 'victory' | 'defeat'

interface SoundConfig {
  src: string[]
  volume: number
  rate?: number
  loop?: boolean
}

const COLLECT_COOLDOWN_MS = 50

const SOUND_CONFIGS: Record<SoundName, SoundConfig> = {
  collect: { src: ['/assets/sfx/collect.mp3', '/assets/sfx/collect.ogg'], volume: 0.4 },
  boost: { src: ['/assets/sfx/boost.mp3', '/assets/sfx/boost.ogg'], volume: 0.5, loop: true },
  death: { src: ['/assets/sfx/death.mp3', '/assets/sfx/death.ogg'], volume: 0.7 },
  kill: { src: ['/assets/sfx/kill.mp3', '/assets/sfx/kill.ogg'], volume: 0.6 },
  portal_enter: { src: ['/assets/sfx/portal_enter.mp3', '/assets/sfx/portal_enter.ogg'], volume: 0.8 },
  portal_spawn: { src: ['/assets/sfx/portal_spawn.mp3', '/assets/sfx/portal_spawn.ogg'], volume: 0.5 },
  zone_warning: { src: ['/assets/sfx/zone_warning.mp3', '/assets/sfx/zone_warning.ogg'], volume: 0.4 },
  zone_damage: { src: ['/assets/sfx/zone_damage.mp3', '/assets/sfx/zone_damage.ogg'], volume: 0.3, loop: true },
  countdown: { src: ['/assets/sfx/countdown.mp3', '/assets/sfx/countdown.ogg'], volume: 0.6 },
  respawn: { src: ['/assets/sfx/respawn.mp3', '/assets/sfx/respawn.ogg'], volume: 0.5 },
  ui_click: { src: ['/assets/sfx/ui_click.mp3', '/assets/sfx/ui_click.ogg'], volume: 0.3 },
  ui_hover: { src: ['/assets/sfx/ui_hover.mp3', '/assets/sfx/ui_hover.ogg'], volume: 0.15 },
  ui_back: { src: ['/assets/sfx/ui_back.mp3', '/assets/sfx/ui_back.ogg'], volume: 0.3 },
  ui_start: { src: ['/assets/sfx/ui_start.mp3', '/assets/sfx/ui_start.ogg'], volume: 0.5 },
  ui_toggle: { src: ['/assets/sfx/ui_toggle.mp3', '/assets/sfx/ui_toggle.ogg'], volume: 0.25 },
}

const MUSIC_CONFIGS: Record<MusicName, SoundConfig> = {
  menu: { src: ['/assets/music/menu.mp3', '/assets/music/menu.ogg'], volume: 0.3, loop: true },
  gameplay: { src: ['/assets/music/gameplay.mp3', '/assets/music/gameplay.ogg'], volume: 0.2, loop: true },
  intense: { src: ['/assets/music/intense.mp3', '/assets/music/intense.ogg'], volume: 0.25, loop: true },
  victory: { src: ['/assets/music/victory.mp3', '/assets/music/victory.ogg'], volume: 0.4 },
  defeat: { src: ['/assets/music/defeat.mp3', '/assets/music/defeat.ogg'], volume: 0.3 },
}

export class AudioManager {
  private static instance: AudioManager

  private sounds: Map<string, Howl> = new Map()
  private music: Map<string, Howl> = new Map()
  private currentMusic: Howl | null = null
  private currentMusicName: string | null = null

  private sfxVolume = 0.7
  private musicVolume = 0.4
  private muted = false
  private lastCollectTime = 0

  private constructor() {
    this.preloadSounds()
    this.preloadMusic()
    this.ensureAudioContext()
  }

  /** Resume AudioContext on first user gesture if browser blocked autoplay. */
  private ensureAudioContext(): void {
    const resume = (): void => {
      const ctx = Howler.ctx
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { /* ignore */ })
      }
      document.removeEventListener('touchstart', resume)
      document.removeEventListener('click', resume)
    }
    document.addEventListener('touchstart', resume, { once: true, passive: true })
    document.addEventListener('click', resume, { once: true })
  }

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager()
    }
    return AudioManager.instance
  }

  private preloadSounds(): void {
    for (const [name, config] of Object.entries(SOUND_CONFIGS)) {
      const howl = new Howl({
        src: config.src,
        volume: config.volume * this.sfxVolume,
        rate: config.rate ?? 1,
        loop: config.loop ?? false,
        preload: true,
        onloaderror: (_id, err) => {
          this.sounds.delete(name)
          this.logLoadError('sfx', name, err)
        },
      })
      this.sounds.set(name, howl)
    }
  }

  private preloadMusic(): void {
    for (const [name, config] of Object.entries(MUSIC_CONFIGS)) {
      const howl = new Howl({
        src: config.src,
        volume: config.volume * this.musicVolume,
        loop: config.loop ?? false,
        preload: false,
        onloaderror: (_id, err) => {
          this.music.delete(name)
          this.logLoadError('music', name, err)
        },
      })
      this.music.set(name, howl)
    }
  }

  private logLoadError(category: string, name: string, err: unknown): void {
    if (typeof globalThis.console !== 'undefined') {
      globalThis.console.warn(`Failed to load ${category}/${name}:`, err)
    }
  }

  play(name: SoundName): void {
    if (this.muted) return

    // Rate-limit collection sounds
    if (name === 'collect') {
      const now = Date.now()
      if (now - this.lastCollectTime < COLLECT_COOLDOWN_MS) return
      this.lastCollectTime = now
    }

    const sound = this.sounds.get(name)
    if (sound) {
      try { sound.play() } catch { /* audio not ready */ }
    }
  }

  stop(name: SoundName): void {
    const sound = this.sounds.get(name)
    if (sound) {
      sound.stop()
    }
  }

  playMusic(name: MusicName): void {
    if (this.currentMusicName === name && this.currentMusic?.playing()) return

    this.stopMusic()

    const track = this.music.get(name)
    if (track) {
      try { track.play() } catch { /* audio not ready */ }
      this.currentMusic = track
      this.currentMusicName = name
    }
  }

  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.fade(this.currentMusic.volume(), 0, 500)
      const fadeTarget = this.currentMusic
      setTimeout(() => fadeTarget.stop(), 500)
      this.currentMusic = null
      this.currentMusicName = null
    }
  }

  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume))

    for (const [name, howl] of this.sounds) {
      const baseVolume = SOUND_CONFIGS[name as SoundName]?.volume ?? 0.5
      howl.volume(baseVolume * this.sfxVolume)
    }
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume))

    for (const [name, howl] of this.music) {
      const baseVolume = MUSIC_CONFIGS[name as MusicName]?.volume ?? 0.3
      howl.volume(baseVolume * this.musicVolume)
    }
  }

  getSfxVolume(): number {
    return this.sfxVolume
  }

  getMusicVolume(): number {
    return this.musicVolume
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    Howler.mute(muted)
  }

  isMuted(): boolean {
    return this.muted
  }

  dispose(): void {
    this.stopMusic()
    for (const howl of this.sounds.values()) {
      howl.unload()
    }
    for (const howl of this.music.values()) {
      howl.unload()
    }
    this.sounds.clear()
    this.music.clear()
  }
}
